import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  useToast
} from '@chakra-ui/react';
import { useBalance } from '../../../../../context/BalanceContext';
import UniversalGameComponent from '../../UniversalGameComponent';
import { convertToUniversalFormat } from '../../Templates/UniversalGameTemplate';

/**
 * CustomGamePage Component
 * 
 * A standalone component for custom user-created games.
 * This version is simplified to just display the card.
 */
const CustomGamePage = () => {
  const { gameId } = useParams();
  const toast = useToast();
  const { balance } = useBalance();
  
  // Game state
  const [gameConfig, setGameConfig] = useState(null);
  const [gameState, setGameState] = useState({
    status: 'active',
    cellValues: Array(9).fill().map(() => Math.floor(Math.random() * 100)),
    revealedCells: [],
    winningCells: [4], // Middle cell is winning
    isWinningCard: Math.random() > 0.7, // 30% chance of winning
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load game config from localStorage
  useEffect(() => {
    console.log("Loading game with ID:", gameId);
    
    // Default Vegas Top game configuration in universal format
    const defaultGameConfig = {
      id: gameId || 'vegas_top',
      title: 'VEGAS TOP',
      description: 'Win big with matching star symbols!',
      theme: {
        colors: {
          primary: '#8B0000', // Red theme
          secondary: '#590000',
          accent: 'yellow.300',
          text: 'white',
          cellBackground: '#e91e63'
        },
        glassmorphism: {
          backdropFilter: "blur(10px)",
          boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.5)",
          border: "1px solid rgba(255, 0, 0, 0.15)"
        }
      },
      card: {
        grid: {
          rows: 3,
          columns: 3
        },
        symbols: {
          winning: "⭐",
          default: "?"
        }
      },
      prizes: {
        tiers: [
          { amount: 1000000, count: 1 }
        ],
        tokenType: 'USDC'
      },
      features: {
        bountyPool: true,
        lottery: false,
        giftOption: true,
        revealAll: true,
        recentWinners: true
      },
      publishedAt: new Date().toISOString()
    };
    
    // Set the default game config and stop loading
    setGameConfig(defaultGameConfig);
    setIsLoading(false);
    
    // Try to load from localStorage (for future reference)
    try {
      console.log("Attempting to load from localStorage");
      const savedGame = localStorage.getItem(`game_${gameId}`);
      console.log("Saved game:", savedGame);
      
      if (savedGame) {
        const parsedGame = JSON.parse(savedGame);
        // Convert to universal format if needed
        const universalConfig = convertToUniversalFormat(parsedGame);
        setGameConfig(universalConfig);
        console.log("Loaded game from localStorage:", universalConfig);
      } else {
        // Try to find in userCreatedGames array
        const userGames = JSON.parse(localStorage.getItem('userCreatedGames') || '[]');
        console.log("User created games:", userGames);
        const foundGame = userGames.find(game => game.id === gameId);
        
        if (foundGame) {
          // Convert to universal format if needed
          const universalConfig = convertToUniversalFormat(foundGame);
          setGameConfig(universalConfig);
          console.log("Found game in userCreatedGames:", universalConfig);
          // Also save it individually for future access
          localStorage.setItem(`game_${gameId}`, JSON.stringify(universalConfig));
        } else {
          console.log("Game not found in localStorage, using default");
        }
      }
    } catch (error) {
      console.error("Error loading game:", error);
    }
  }, [gameId, toast]);

  // Handle revealing a cell
  const handleReveal = (cellIndex) => {
    if (!gameState) return;
    
    // Check if cell is already revealed
    if (gameState.revealedCells.includes(cellIndex)) {
      return;
    }
    
    // Create a copy of the game state
    const newState = { ...gameState };
    
    // Add cell to revealed cells
    newState.revealedCells.push(cellIndex);
    
    // Check if all cells are revealed
    if (newState.revealedCells.length === gameState.cellValues.length) {
      newState.status = 'complete';
    }
    
    // Update the game state
    setGameState(newState);
  };

  // If game is still loading or not found, show loading state
  if (isLoading || !gameConfig) {
    return (
      <Box width="100%" height="100vh" display="flex" justifyContent="center" alignItems="center">
        {isLoading ? "Loading game..." : "Game not found"}
      </Box>
    );
  }

  // Ensure gameConfig.theme exists before accessing it
  const bgGradient = gameConfig && gameConfig.theme && gameConfig.theme.colors ? 
    `linear-gradient(135deg, ${gameConfig.theme.colors.primary} 0%, ${gameConfig.theme.colors.secondary} 100%)` :
    'linear-gradient(135deg, #8B0000 0%, #590000 100%)';

  return (
    <Box 
      width="100%" 
      minHeight="100vh" 
      bgGradient={bgGradient}
      py={8}
    >
      <Box maxWidth="container.xl" mx="auto">
        <UniversalGameComponent 
          gameConfig={gameConfig}
        />
      </Box>
    </Box>
  );
};

export default CustomGamePage;
